import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@core/components/ui/alert-dialog";
import { Checkbox } from "@core/components/ui/checkbox";
import { Input } from "@core/components/ui/input";
import { Label } from "@core/components/ui/label";
import { Textarea } from "@core/components/ui/textarea";
import { useToast } from "@core/hooks/use-toast";
import { supabase } from "@core/lib/supabase";
import { useAuth } from "@core/contexts/AuthContext";
import { logError } from "@core/lib/error-handling";

interface DeleteAccountModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Appelé après suppression réussie + logout.
   * Par défaut redirige vers la landing.
   */
  onDeleted?: () => void;
}

/**
 * Modal de suppression définitive du compte (MVP).
 *
 * Double confirmation :
 *   1. Case "je comprends que c'est irréversible"
 *   2. Saisie du mot "DELETE" (case-sensitive)
 *
 * Sans ces deux conditions, le bouton rouge reste désactivé.
 * Suppression immédiate via edge function `delete-account`.
 * La grâce 30j est reportée en v1.1 (cf. docs/specifications/pr4-suppression-compte.md).
 */
export default function DeleteAccountModal({
  open,
  onClose,
  onDeleted,
}: DeleteAccountModalProps) {
  const { t } = useTranslation("common");
  const { toast } = useToast();
  const { user, profile } = useAuth();
  const [acknowledged, setAcknowledged] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [reason, setReason] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const canDelete = acknowledged && confirmation === "DELETE" && !isDeleting;

  async function handleConfirmDelete() {
    if (!canDelete) return;
    setIsDeleting(true);

    try {
      const { data, error } = await supabase.functions.invoke(
        "delete-account",
        {
          body: {
            confirmation: "DELETE",
            reason: reason.trim() || null,
          },
        },
      );

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({
        title: t("account.delete.success_title"),
        description: t("account.delete.success_description"),
        duration: 4000,
      });

      // Logout local (l'user n'existe déjà plus côté serveur) puis redirect
      await supabase.auth.signOut().catch(() => {
        /* user déjà supprimé, ignore error */
      });

      // Laisse le toast s'afficher brièvement
      setTimeout(() => {
        if (onDeleted) {
          onDeleted();
        } else {
          window.location.href = "/";
        }
      }, 1500);
    } catch (err) {
      logError(
        new Error(
          `Account deletion failed for ${user?.email ?? "unknown"}: ${
            err instanceof Error ? err.message : String(err)
          }`,
        ),
      );
      toast({
        title: t("account.delete.error_title"),
        description: t("account.delete.error_description"),
        duration: 6000,
      });
      setIsDeleting(false);
    }
  }

  function handleCloseReset() {
    if (isDeleting) return; // empêche la fermeture pendant la suppression
    setAcknowledged(false);
    setConfirmation("");
    setReason("");
    onClose();
  }

  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && handleCloseReset()}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-red-500">
            <AlertTriangle className="w-5 h-5" />
            {t("account.delete.title")}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-3 pt-2">
            <span className="block">
              {t("account.delete.intro_prefix")}{" "}
              <strong>{t("account.delete.intro_emphasis")}</strong>.
            </span>
            <span className="block">{t("account.delete.will_be_removed")}</span>
            <ul className="list-disc list-inside text-sm space-y-1 ml-2">
              <li>{t("account.delete.item_profile")}</li>
              <li>{t("account.delete.item_polls")}</li>
              <li>{t("account.delete.item_ai")}</li>
            </ul>
            <span className="block text-sm text-gray-400 mt-2">
              {t("account.delete.votes_anonymized_prefix")}{" "}
              <strong>{t("account.delete.votes_anonymized_emphasis")}</strong>
              {t("account.delete.votes_anonymized_suffix")}
            </span>
            {profile?.plan_type && profile.plan_type !== "free" && (
              <span className="block text-sm text-amber-400 mt-2">
                {t("account.delete.subscription_warning", {
                  plan: profile.plan_type,
                })}
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-start gap-3">
            <Checkbox
              id="acknowledge-deletion"
              checked={acknowledged}
              onCheckedChange={(v) => setAcknowledged(v === true)}
              disabled={isDeleting}
              data-testid="delete-account-acknowledge"
            />
            <Label
              htmlFor="acknowledge-deletion"
              className="text-sm cursor-pointer leading-snug"
            >
              {t("account.delete.acknowledge_label")}
            </Label>
          </div>

          <div className="space-y-1">
            <Label htmlFor="delete-confirmation" className="text-sm">
              {t("account.delete.confirm_prompt_prefix")}{" "}
              <code className="text-red-400 font-mono font-bold">DELETE</code>
              {t("account.delete.confirm_prompt_suffix")}
            </Label>
            <Input
              id="delete-confirmation"
              type="text"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              disabled={isDeleting}
              autoComplete="off"
              data-testid="delete-account-confirm-input"
              placeholder="DELETE"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="deletion-reason" className="text-sm text-gray-400">
              {t("account.delete.reason_label")}
            </Label>
            <Textarea
              id="deletion-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isDeleting}
              rows={2}
              maxLength={500}
              placeholder={t("account.delete.reason_placeholder")}
              data-testid="delete-account-reason"
            />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>
            {t("account.delete.cancel")}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirmDelete}
            disabled={!canDelete}
            className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            data-testid="delete-account-confirm-button"
          >
            {isDeleting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t("account.delete.deleting")}
              </>
            ) : (
              t("account.delete.confirm_button")
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
